import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiPost } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

const MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

function validatePassword(password) {
  if (!password || password.length < 10) return 'Password must be at least 10 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
}

function PasswordInput({ value, onChange, ...rest }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        style={{ width: '100%', paddingRight: 40, boxSizing: 'border-box' }}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
        tabIndex={-1}
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          display: 'flex', alignItems: 'center', color: 'var(--ink-lt)', lineHeight: 0
        }}
      >
        {show ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg]     = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await apiPost('/auth/login', { email, password });
      login(data.token, data.user);
      window.location.href = data.user.role === 'vendor' ? '/vendor' : '/admin';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await apiPost('/auth/forgot-password', { email: forgotEmail });
      setForgotMsg('If an account exists for that email, a password reset link has been sent. Please check your inbox.');
    } catch (err) {
      setForgotMsg('If an account exists for that email, a password reset link has been sent. Please check your inbox.');
    } finally {
      setForgotLoading(false);
    }
  }

  if (forgotMode) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <div className="emblem">💧</div>
            <h1>Advanced Waste Water Treatment Corp</h1>
            <p>Waste Profile Management Portal</p>
          </div>
          <h2>Forgot Password</h2>
          {forgotMsg ? (
            <div>
              <div className="alert alert-success">{forgotMsg}</div>
              <button className="btn btn-secondary" style={{ width: '100%', marginTop: 12 }}
                onClick={() => { setForgotMode(false); setForgotMsg(''); setForgotEmail(''); }}>
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="form-grid">
              <div className="form-group">
                <label>Email address</label>
                <input type="email" value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)} required autoFocus />
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--ink-lt)', lineHeight: 1.6 }}>
                Enter your email address and we'll send you a link to reset your password. The link will expire in 1 hour.
              </p>
              <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                {forgotLoading ? 'Sending…' : 'Send Reset Link'}
              </button>
              <button type="button" className="btn btn-secondary"
                onClick={() => setForgotMode(false)}>← Back to Sign In</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="emblem">💧</div>
          <h1>Advanced Waste Water Treatment Corp</h1>
          <p>Waste Profile Management Portal</p>
        </div>
        <h2>Sign In</h2>

        {/* ── Maintenance banner ── */}
        {MAINTENANCE && (
          <div style={{
            background: '#fff3e0',
            border: '1px solid #ffcc80',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            fontSize: 13,
            color: '#e65100',
          }}>
            <span style={{ fontSize: 16, lineHeight: 1.3 }}>🔧</span>
            <span>
              <strong>Scheduled Maintenance</strong><br />
              The portal is temporarily offline for maintenance. Please check back shortly.
            </span>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label>Email address</label>
            <input type="email" value={email}
              onChange={e => setEmail(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div className="auth-footer" style={{ marginTop: 12 }}>
          <button type="button" onClick={() => setForgotMode(true)}
            style={{ background: 'none', border: 'none', color: 'var(--forest-lt)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
            Forgot your password?
          </button>
        </div>
        <div className="auth-footer" style={{ marginTop: 8 }}>
          New vendor? <Link to="/register">Create an account</Link>
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }
    setError(''); setLoading(true);
    try {
      await apiPost('/auth/reset-password', { token, password });
      setSuccess('Password reset successfully! You can now sign in with your new password.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <div className="emblem">💧</div>
            <h1>Advanced Waste Water Treatment Corp</h1>
            <p>Waste Profile Management Portal</p>
          </div>
          <div className="alert alert-error">Invalid or missing reset token. Please request a new password reset.</div>
          <Link to="/login" className="btn btn-secondary" style={{ marginTop: 12, display: 'block', textAlign: 'center' }}>
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="emblem">💧</div>
          <h1>Advanced Waste Water Treatment Corp</h1>
          <p>Waste Profile Management Portal</p>
        </div>
        <h2>Reset Password</h2>
        {success ? (
          <div>
            <div className="alert alert-success">{success}</div>
            <Link to="/login" className="btn btn-primary" style={{ marginTop: 12, display: 'block', textAlign: 'center' }}>
              Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="form-grid">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label>New Password</label>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
              <span className="form-hint">Minimum 10 characters, with at least one uppercase letter and one number</span>
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} required />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
            <Link to="/login" style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-lt)' }}>
              Back to Sign In
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '', firstName: '', lastName: '', companyName: '', phone: ''
  });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const pwError = validatePassword(form.password);
    if (pwError) {
      setError(pwError);
      return;
    }
    setError(''); setLoading(true);
    try {
      const { confirmPassword, ...payload } = form;
      const data = await apiPost('/auth/register', payload);
      login(data.token, data.user);
      window.location.href = '/vendor';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 500 }}>
        <div className="auth-logo">
          <div className="emblem">💧</div>
          <h1>Create Vendor Account</h1>
          <p>Waste Profile Management Portal</p>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label>First name <span className="req">*</span></label>
              <input value={form.firstName} onChange={set('firstName')} required />
            </div>
            <div className="form-group">
              <label>Last name <span className="req">*</span></label>
              <input value={form.lastName} onChange={set('lastName')} required />
            </div>
          </div>
          <div className="form-group">
            <label>Company name <span className="req">*</span></label>
            <input value={form.companyName} onChange={set('companyName')} required />
          </div>
          <div className="form-group">
            <label>Email address <span className="req">*</span></label>
            <input type="email" value={form.email} onChange={set('email')} required />
          </div>
          <div className="form-group">
            <label>Phone number</label>
            <input type="tel" value={form.phone} onChange={set('phone')} />
          </div>
          <div className="form-group">
            <label>Password <span className="req">*</span></label>
            <PasswordInput value={form.password} onChange={set('password')} required minLength={10} />
            <span className="form-hint">Minimum 10 characters, with at least one uppercase letter and one number</span>
          </div>
          <div className="form-group">
            <label>Confirm Password <span className="req">*</span></label>
            <PasswordInput value={form.confirmPassword} onChange={set('confirmPassword')} required minLength={10} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
